// Landing garden — the growing flower-hill animation under the welcome card.
//
// Replaces the static seven-stage row (kept in the markup as the no-JS /
// reduced-motion fallback) with a live retelling of the caption: one word is
// planted centre-stage, grows through every stage of the real garden sprite
// and blooms; then more words follow all over a gentle hill, arriving faster
// and faster — each growing at the SAME unhurried pace — until the slope is
// covered edge-to-edge in glowing, sparkling flowers. Four depth rows fake
// perspective (back = smaller, higher, behind), and the hill silhouette is
// drawn from the SAME curve that seats the plants, so they stand on it at
// every width.
//
// Every flower IS a word: as it sprouts its second leaves, a small chip pops
// up for 1.5s with a starter word in one of the offered target languages
// (hardcoded list — this is theatre, not the dictionary), and hovering a
// grown flower brings its word back. The centre flower alone speaks the PAGE's
// language: it grows the hero headword's gloss ("flower garden"), the same word
// the line above is naming in every language on offer.
//
// A finished hill is clickable and sows itself again from the first seed.
//
// Purely decorative (aria-hidden); every failure path (no sprite, reduced
// motion, an exception mid-build) leaves the static row exactly as it was.
//
// Sprite mechanism as in garden.js: fetched and inlined, because an external
// <use href="file#id"> does not resolve the shared <defs> gradients.
(function () {
    "use strict";

    var SPRITE_URL = "/image/garden/garden.svg";

    // One growth ladder: bare mound (a seed just planted) → germinating shoot →
    // the seven stages. Stage 7 carries its halo + sparks in the sprite itself;
    // on top of that, bloom adds the overlay glow burst + sparkle twinkle.
    var BODY_IDS = ["", "g-germ-body", "g-plant-1", "g-plant-2", "g-plant-3",
                    "g-plant-4", "g-plant-5", "g-plant-6", "g-plant-7"];
    var BLOOM = BODY_IDS.length - 1;
    var CHIP_STAGE = 3;    // g-plant-2, the two-leaf sprout: the word arrives

    var SETTLE_MS = 450;   // page-settle pause before the first seed drops
    var MOUND_MS = 320;    // bare-mound beat before germination
    var STAGE_MS = 420;    // per-stage dwell — constant for EVERY plant: the
                           // acceleration is in how often plants appear, never
                           // in how fast one grows
    var GROW_MS = MOUND_MS + (BLOOM - 1) * STAGE_MS;   // planted → bloomed
    var BEAT_MS = 600;     // admire the first bloom before the garden joins in
    var GAP0_MS = 900;     // first follower gap …
    var GAP_RATIO = 0.76;  // … each next one sooner …
    var GAP_MIN_MS = 45;   // … down to a shower of seeds
    var FADE_MS = 260;     // stage fade-in (over the old art — see advance())
    var CHIP_MS = 1500;    // how long a word chip announces itself

    // Hardcoded starter words — simple, unambiguous A1 nouns / verbs /
    // adjectives in the two target languages the CTA offers. Decks reshuffle
    // and cycle if the hill holds more flowers than a list has words.
    var WORDS = {
        de: ["Haus", "Baum", "Wasser", "Brot", "Hund", "Katze", "Sonne", "Mond",
             "Buch", "Tisch", "Apfel", "Blume", "Garten", "Kind", "Tag", "Nacht",
             "Jahr", "Stuhl", "gehen", "kommen", "essen", "trinken", "schlafen",
             "lernen", "spielen", "lesen", "groß", "klein", "alt", "neu", "gut",
             "schnell", "rot", "grün", "blau", "warm"],
        es: ["casa", "árbol", "agua", "pan", "perro", "gato", "sol", "luna",
             "libro", "mesa", "manzana", "flor", "jardín", "niño", "día", "noche",
             "año", "silla", "ir", "venir", "comer", "beber", "dormir",
             "aprender", "jugar", "leer", "grande", "pequeño", "viejo", "nuevo",
             "bueno", "rápido", "rojo", "verde", "azul", "caliente"]
    };

    // The hill: rows of plants seated on one dome. `lift` is each row's base
    // height up the band (fraction of band height), HILL_A the dome's rise.
    // Front row plants are full-size, the farthest under half — cheap
    // perspective. `PACK` is the fraction of a plant's width per slot pitch;
    // rows tile the full strip and are never trimmed, so the finished hill
    // has no holes.
    var ROWS = [
        { lift: 0.48, scale: 0.44, cap: 40 },   // far back
        { lift: 0.33, scale: 0.56, cap: 36 },   // back
        { lift: 0.17, scale: 0.74, cap: 30 },   // mid
        { lift: 0.02, scale: 1.00, cap: 22 }    // front
    ];
    var HILL_A = 0.14;
    var PACK = 0.42;

    var GLINTS = 3;        // sparkles per flower, each with its own place and rhythm
    var ADVANCE_PER_FRAME = 6;   // stage changes allowed in one frame — see tick()

    var rand = Math.random;

    // The centre flower's word: the gloss from the hero's dictionary line, carried on
    // the element as a data attribute so this does not have to unpick the "— " prefix
    // the visible text wears. Falls back to the English wording if the line is gone.
    function heroGloss() {
        var el = document.querySelector(".landingEntryGlossClass");
        var g = (el && el.getAttribute("data-gloss") || "").trim();
        return g || "flower garden";
    }

    // One sparkle, scattered around the bloom head. Everything that must differ per
    // sparkle rides inline; the CSS only holds the flash keyframes.
    function glintHtml() {
        return '<div class="landingGlintClass" style="'
            + "left:" + (16 + rand() * 68).toFixed(0) + "%;"
            + "top:" + (6 + rand() * 44).toFixed(0) + "%;"
            + "--glint-size:" + (0.2 + rand() * 0.18).toFixed(2) + ";"
            + "--glint-dur:" + (2.4 + rand() * 3.2).toFixed(2) + "s;"
            + "--glint-delay:" + (rand() * 4).toFixed(2) + "s;"
            + '">✦</div>';
    }

    function dome(x) {   // x in 0..100 → 0..1, peaking at the centre
        var d = (x - 50) / 52;
        return Math.max(0, 1 - d * d);
    }

    function injectSprite(svgText) {
        // No display:none on the holder — filtered art inside a display:none
        // subtree does not paint through <use>. The sprite's root svg hides
        // itself (width=0 height=0 position:absolute).
        var holder = document.createElement("div");
        holder.setAttribute("aria-hidden", "true");
        holder.innerHTML = svgText;
        document.body.insertBefore(holder, document.body.firstChild);
    }

    // The hill silhouette in band-percent space: three soft green layers laced
    // between the plant rows, all on the same dome the plants stand on.
    function hillPath(lift) {
        var d = "M0 100";
        for (var x = 0; x <= 100; x += 5) {
            d += " L" + x + " " + (100 - (lift + HILL_A * dome(x)) * 100).toFixed(1);
        }
        return d + " L100 100 Z";
    }

    function hillSvg() {
        return '<svg class="landingHillClass" viewBox="0 0 100 100" preserveAspectRatio="none">'
            + '<path d="' + hillPath(0.40) + '" fill="hsl(97 28% 89%)" opacity=".45"></path>'
            + '<path d="' + hillPath(0.22) + '" fill="hsl(97 30% 86%)" opacity=".55"></path>'
            + '<path d="' + hillPath(0.015) + '" fill="hsl(97 33% 83%)" opacity=".7"></path>'
            + '</svg>';
    }

    var decks = { de: [], es: [] };
    function dealWord(lang) {
        var deck = decks[lang];
        if (!deck.length) {
            deck = decks[lang] = WORDS[lang].slice();
            for (var i = deck.length - 1; i > 0; i--) {
                var k = Math.floor(rand() * (i + 1));
                var t = deck[i]; deck[i] = deck[k]; deck[k] = t;
            }
        }
        return deck.pop();
    }

    // The final layout is decided up front: per-row jittered slot centres that
    // tile the whole hill (so "filled" ends with no gaps), then visited in
    // shuffled order (so the filling reads as random).
    function makeSlots(bandW, plantW) {
        var slots = [], i, r;
        for (r = 0; r < ROWS.length; r++) {
            var row = ROWS[r];
            var n = Math.max(6, Math.min(row.cap, Math.round(bandW / (plantW * row.scale * PACK))));
            var pitch = 100 / n;
            for (i = 0; i < n; i++) {
                var x = (i + 0.5) * pitch + (rand() - 0.5) * pitch * 0.4;
                slots.push({
                    row: r,
                    x: x,
                    bottom: (row.lift + HILL_A * dome(x)) * 100 + (rand() - 0.5) * 3,
                    scale: row.scale * (1 + (rand() - 0.5) * 0.08),
                    z: 10 + r * 20 + (i % 9),
                    stage: -1
                });
            }
        }
        for (i = slots.length - 1; i > 0; i--) {   // Fisher–Yates
            var k = Math.floor(rand() * (i + 1));
            var t = slots[i]; slots[i] = slots[k]; slots[k] = t;
        }
        // The protagonist: the front-row slot nearest the centre, straightened
        // out to exactly centre-stage, full size, and first to grow.
        var front = ROWS.length - 1, c = -1;
        for (i = 0; i < slots.length; i++) {
            if (slots[i].row !== front) { continue; }
            if (c < 0 || Math.abs(slots[i].x - 50) < Math.abs(slots[c].x - 50)) { c = i; }
        }
        if (c < 0) { c = 0; }   // cannot happen (every row keeps ≥6 slots) — belt+braces
        var centre = slots.splice(c, 1)[0];
        centre.x = 50;
        centre.bottom = (ROWS[centre.row].lift + HILL_A) * 100;
        centre.scale = ROWS[centre.row].scale;
        centre.z = 90;
        slots.unshift(centre);

        var at = SETTLE_MS, gap = GAP0_MS;
        for (i = 0; i < slots.length; i++) {
            var s = slots[i];
            s.spawnAt = at;
            if (i === 0) {
                s.lang = "page"; s.word = heroGloss();   // the header's own gloss
                at += GROW_MS + BEAT_MS;
            } else {
                s.lang = rand() < 0.5 ? "de" : "es";
                s.word = dealWord(s.lang);
                at += gap; gap = Math.max(GAP_MIN_MS, gap * GAP_RATIO);
            }
        }
        return slots;
    }

    function stageAt(dt) {
        if (dt < 0) { return -1; }
        if (dt < MOUND_MS) { return 0; }
        return Math.min(BLOOM, 1 + Math.floor((dt - MOUND_MS) / STAGE_MS));
    }

    // ── word chips ───────────────────────────────────────────────────────────
    // While a chip is up its slot out-stacks the neighbouring plants; the
    // resting z comes back once the fade-out ends.
    //
    // ⚠️ AUTO chips are rationed, hover chips never are (F19). The gaps between
    // arrivals shrink geometrically — 900 ms down to 45 ms — so by the closing
    // shower dozens of plants reach the two-leaf stage inside one chip's 1.5 s
    // life, and on a phone their labels landed on top of each other: an
    // unreadable pile, which is the opposite of "every flower IS a word". A
    // chip nobody can read announces nothing, so past the cap the flower simply
    // grows in silence and keeps its word for a hover. Early on, when arrivals
    // are seconds apart, the cap is never reached and every flower still speaks.
    var MAX_AUTO_CHIPS = 3;
    var liveAutoChips = 0;

    function showChip(slot, auto) {
        if (auto && liveAutoChips >= MAX_AUTO_CHIPS) { return; }
        if (slot.chipTimer) { clearTimeout(slot.chipTimer); slot.chipTimer = 0; }
        slot.el.style.zIndex = 500;
        slot.chip.classList.add("show");
        if (auto) {
            liveAutoChips++;
            slot.autoChip = true;
            slot.chipTimer = setTimeout(function () {
                slot.chipTimer = 0;
                if (!slot.hovered) { hideChip(slot); }
            }, CHIP_MS);
        }
    }

    function hideChip(slot) {
        if (slot.autoChip) { slot.autoChip = false; liveAutoChips--; }
        slot.chip.classList.remove("show");
        setTimeout(function () {
            if (!slot.chip.classList.contains("show")) { slot.el.style.zIndex = slot.zHome; }
        }, 260);
    }

    // Move a slot to `stage` and animate the step. The incoming art fades in
    // OVER the outgoing, which stays fully painted underneath until covered —
    // never a symmetric cross-fade, whose half-way opacity dip made the soil
    // collar and stem visibly blink between stages. No per-stage pops either:
    // the plant's ground furniture holds perfectly still while it grows.
    // A hidden-tab pause can skip several stages at once — land, no theatre.
    function advance(slot, stage) {
        var from = slot.stage;
        slot.stage = stage;
        var jump = from >= 0 && stage - from > 1;
        if (from < 0) { slot.el.style.visibility = ""; }
        if (stage >= 1) {
            var out = slot.uses[slot.cur];
            var inc = slot.uses[slot.cur ^= 1];
            inc.setAttribute("href", "#" + BODY_IDS[stage]);
            slot.svg.appendChild(inc);          // paint over the outgoing layer
            inc.style.opacity = "1";
            if (!jump && inc.animate) {
                inc.animate([
                    { opacity: 0, transform: "translateY(2.5px)" },
                    { opacity: 1, transform: "translateY(0)" }
                ], { duration: FADE_MS, easing: "ease-out" });
                setTimeout(function () { out.style.opacity = "0"; }, FADE_MS);
            } else {
                out.style.opacity = "0";
            }
        }
        if (from < 0 && slot.svg.animate && !jump) {
            slot.svg.animate([
                { transform: "scale(.45)", opacity: 0 },
                { transform: "scale(1.06)", opacity: 1, offset: .7 },
                { transform: "scale(1)" }
            ], { duration: 300, easing: "ease-out" });
        }
        if (stage >= CHIP_STAGE && !slot.chipShown) {
            slot.chipShown = true;              // hover works from here on
            if (!jump) { showChip(slot, true); }
        }
        if (stage >= BLOOM) {
            // .lit starts the CSS twinkle + sparkle; the one-shot burst rides
            // on top of it (WAAPI wins while it runs, then hands back).
            slot.el.classList.add("lit");
            if (!jump && slot.glow.animate) {
                slot.glow.animate([
                    { opacity: 0, transform: "translate(-50%,-50%) scale(.3)" },
                    { opacity: 1, transform: "translate(-50%,-50%) scale(1.35)", offset: .5 },
                    { opacity: .6, transform: "translate(-50%,-50%) scale(1)" }
                ], { duration: 650, easing: "ease-out" });
                slot.svg.animate([
                    { transform: "scale(.98)" },
                    { transform: "scale(1.07)", offset: .55 },
                    { transform: "scale(1)" }
                ], { duration: 420, easing: "ease-out" });
            }
        }
    }

    // One garden, grown once and re-growable: a finished hill is clickable and
    // starts over from the first seed. Sizes are re-measured on every sowing (the
    // window may have been resized since), which means briefly un-hiding the static
    // row — the only element whose natural plant width is readable. Same task, so
    // nothing paints in between.
    function buildGarden(garden, row, rowPlant) {
        row.style.display = "";
        var bandW = garden.clientWidth;
        var plantW = rowPlant.getBoundingClientRect().width;
        row.style.display = "none";   // display, not [hidden] — the row is display:flex
        if (!bandW || !plantW) { return null; }   // hidden / zero-size edge

        // Built fully detached; nothing is inserted until everything stands.
        var band = document.createElement("div");
        band.className = "landingGardenBandClass";
        band.setAttribute("aria-hidden", "true");
        band.innerHTML = hillSvg();
        var slots = makeSlots(bandW, plantW);
        slots.forEach(function (slot) {
            var el = document.createElement("div");
            el.className = "landingBandSlotClass";
            el.style.left = slot.x + "%";
            el.style.bottom = slot.bottom + "%";
            el.style.zIndex = slot.z;
            slot.zHome = String(slot.z);
            el.style.setProperty("--slot-scale", slot.scale.toFixed(3));
            el.style.setProperty("--tw-dur", (3.2 + rand() * 2).toFixed(2) + "s");
            el.style.setProperty("--tw-delay", (rand() * 2.5).toFixed(2) + "s");
            el.style.visibility = "hidden";
            var glints = "";
            for (var g = 0; g < GLINTS; g++) { glints += glintHtml(); }
            // innerHTML, not createElement: the foreign-content parser makes real
            // SVG nodes (same route the app's garden code takes). Two body <use>
            // layers alternate as fade partners; the sparkles and the chip ride
            // above it all.
            el.innerHTML = '<div class="landingBloomGlowClass"></div>'
                + '<svg viewBox="24 2 80 98"><use href="#g-mound"></use>'
                + '<use style="opacity:0"></use><use style="opacity:0"></use></svg>'
                + glints
                + '<span class="landingWordChipClass"></span>';
            slot.el = el;
            slot.svg = el.querySelector("svg");
            slot.glow = el.querySelector(".landingBloomGlowClass");
            slot.chip = el.querySelector(".landingWordChipClass");
            slot.chip.textContent = slot.word;
            var uses = el.querySelectorAll("use");
            slot.uses = [uses[1], uses[2]];
            slot.cur = 0;
            // Hover brings a flower's word back — but only once it has
            // introduced itself. Hits land on painted plant pixels only
            // (pointer-events in CSS), so back-row flowers stay reachable
            // between their bigger front-row neighbours.
            el.addEventListener("mouseenter", function () {
                if (!slot.chipShown) { return; }
                slot.hovered = true;
                showChip(slot, false);
            });
            el.addEventListener("mouseleave", function () {
                slot.hovered = false;
                if (!slot.chipTimer) { hideChip(slot); }
            });
            band.appendChild(el);
        });
        garden.insertBefore(band, row);

        var t0 = -1;                  // clock starts at the first PAINTED frame, so a
        var left = slots.length;      // tab opened in the background plays from the top

        // A hidden tab produces no animation frames at all, so the clock would
        // otherwise run on without the theatre and the visitor would come back
        // to a hill that had "grown" in one jump (advance() lands those without
        // theatre, deliberately). Charging the hidden stretch back to t0 makes
        // the garden PAUSE instead: come back, and it carries on from the flower
        // it had reached. Same show, just not played to an empty room.
        var hiddenAt = 0;
        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                hiddenAt = performance.now();
            } else if (hiddenAt) {
                if (t0 >= 0) { t0 += performance.now() - hiddenAt; }
                hiddenAt = 0;
            }
        });

        function tick(now) {
            if (t0 < 0) { t0 = now; }
            var t = now - t0;
            // ⚠️ At most BUDGET plants change stage in any one frame. The gaps
            // between arrivals shrink to 45 ms, so in the closing shower dozens
            // of plants come due on the SAME frame — and each stage change costs
            // style recalc and layout across the whole band. Unbounded, that is
            // one 600 ms task that a phone cannot paint or respond through (it
            // measured as 635 ms of blocking time). Bounded, it is a run of
            // short ones: the same flowers, the same order, the same clock, each
            // arriving at most a frame or two later than its slot said. Nothing
            // a person can see — the growth itself is 420 ms per stage.
            var budget = ADVANCE_PER_FRAME;
            for (var i = 0; i < slots.length; i++) {
                var slot = slots[i];
                if (slot.stage >= BLOOM) { continue; }
                var stage = stageAt(t - slot.spawnAt);
                if (stage > slot.stage) {
                    advance(slot, stage);
                    if (slot.stage >= BLOOM) { left--; }
                    if (--budget <= 0) { break; }
                }
            }
            if (left > 0) { requestAnimationFrame(tick); }
            else { band.dataset.abloom = "1"; }   // CSS fades in the shared glow + cursor
        }
        requestAnimationFrame(tick);
        return band;
    }

    // Keep the band's forever-animations (a halo and three sparkles per flower)
    // off the main thread whenever the strip is not on screen. Measured on a
    // 4x-throttled phone: the band is the ENTIRE main-thread cost of this page
    // — 4.3 s of style recalc and 2.4 s of layout against 0.1 s / 0.5 s for the
    // whole rest of the document — and a scrolled-past hill went on paying it.
    // Nothing about the look changes; a paused animation resumes mid-stride.
    function watchVisibility(band) {
        if (!("IntersectionObserver" in window)) { return; }
        var io = new IntersectionObserver(function (entries) {
            band.classList.toggle("landingBandRestingClass", !entries[0].isIntersecting);
        }, { rootMargin: "120px" });
        io.observe(band);
    }

    function plantGarden() {
        var garden = document.querySelector(".landingGardenClass");
        var row = garden && garden.querySelector(".landingGardenRowClass");
        var rowPlant = row && row.querySelector(".pl-plant");
        if (!rowPlant) { return; }

        var band = buildGarden(garden, row, rowPlant);
        if (!band) { row.style.display = ""; return; }   // keep the static row
        watchVisibility(band);

        // Sow it again. Gated on [data-abloom] — the hill is only offered as a
        // button once it is finished, so a click cannot cut a growth short or
        // leave a second clock running beside the first. The old band goes with
        // its slots, chip timers and animations; the fresh one re-randomises
        // positions and words, so no two gardens grow the same.
        garden.addEventListener("click", function (ev) {
            if (!band || !band.dataset.abloom || !band.contains(ev.target)) { return; }
            var old = band;
            band = buildGarden(garden, row, rowPlant) || old;
            if (band !== old) { old.remove(); watchVisibility(band); }
        });
    }

    // ── when the garden is allowed to start ──────────────────────────────────
    // It is decoration, and it is expensive decoration: on a 4x-throttled phone
    // this band is the whole main-thread bill of the landing page. So it waits
    // for three things, in order — the page to finish loading, the main thread
    // to go quiet, and the strip to actually be somewhere a person can see it.
    //
    // What this does NOT change: the show itself. Whoever looks at the band
    // watches the identical growth from the first seed, at the same pace, with
    // the same number of flowers. What changes is that a phone — where the
    // strip sits below the fold — no longer plays the entire animation unseen
    // while the page above it is still painting.

    function whenIdle(fn) {
        if (window.requestIdleCallback) { window.requestIdleCallback(fn, { timeout: 3000 }); }
        else { setTimeout(fn, 250); }
    }

    function afterLoad(fn) {
        if (document.readyState === "complete") { fn(); }
        else { window.addEventListener("load", fn, { once: true }); }
    }

    // Fires once, as soon as the element is within a screen of the viewport.
    // No IntersectionObserver (or no element) ⇒ run anyway: never let a missing
    // API be the reason the garden does not grow.
    function whenNear(el, fn) {
        if (!el || !("IntersectionObserver" in window)) { fn(); return; }
        var io = new IntersectionObserver(function (entries) {
            if (!entries[0].isIntersecting) { return; }
            io.disconnect();
            fn();
        }, { rootMargin: "100% 0px" });
        io.observe(el);
    }

    function start() {
        fetch(SPRITE_URL)
            .then(function (r) { return r.ok ? r.text() : ""; })
            .then(function (svgText) {
                if (!svgText) { return; }   // strip stays blank, as before
                injectSprite(svgText);
                if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { return; }
                try { plantGarden(); } catch (e) { /* static row remains */ }
            })
            .catch(function () { /* strip stays blank, as before */ });
    }

    afterLoad(function () {
        whenNear(document.querySelector(".landingGardenClass"), function () {
            whenIdle(start);
        });
    });
})();
